import { Button } from "@repo/ui/components/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/Card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@repo/ui/components/Chart";
import { Field } from "@repo/ui/components/Field";
import { Input } from "@repo/ui/components/Input";
import { Label } from "@repo/ui/components/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/Select";
import { X } from "lucide-react";
import { useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { trpc } from "../lib/trpc";

export const Dashboard = () => {
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    gameVersion: "",
  });

  const { data: gameVersions } = trpc.analytics.getGameVersions.useQuery();

  const { data: eventsByDay, isLoading: loadingByDay } =
    trpc.analytics.getEventCountsByDay.useQuery({
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      gameVersion: filters.gameVersion || undefined,
    });

  const { data: eventTypes, isLoading: loadingTypes } =
    trpc.analytics.getEventTypeDistribution.useQuery({
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      gameVersion: filters.gameVersion || undefined,
    });

  const { data: playerStats, isLoading: loadingPlayers } =
    trpc.analytics.getPlayerTypeStats.useQuery({
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      gameVersion: filters.gameVersion || undefined,
    });

  return (
    <div className="flex flex-1 flex-col gap-8 p-8">
      <div>
        <h1 className="text-3xl font-bold">Glitch Hunt</h1>
        <p className="text-muted-foreground">Game analytics and player insights</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter analytics by date range</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-4">
          <Field className="flex-1">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="datetime-local"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </Field>
          <Field className="flex-1">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              type="datetime-local"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </Field>
          <Field className="flex-1">
            <Label htmlFor="gameVersion">Game Version</Label>
            <Select
              value={filters.gameVersion}
              onValueChange={(value: string) => setFilters({ ...filters, gameVersion: value })}
            >
              <SelectTrigger id="gameVersion">
                <SelectValue placeholder="All versions" />
              </SelectTrigger>
              <SelectContent>
                {gameVersions?.map((version) => (
                  <SelectItem key={version} value={version}>
                    {version}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-end">
            <Button
              variant="outline"
              color="destructive"
              onClick={() => setFilters({ startDate: "", endDate: "", gameVersion: "" })}
            >
              <X />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Events Over Time */}
      <Card>
        <CardHeader>
          <CardTitle>Events Over Time</CardTitle>
          <CardDescription>Total events per day</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingByDay ? (
            <div className="flex h-72 items-center justify-center">Loading...</div>
          ) : (
            <ChartContainer className="h-72 w-full" config={{}}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={eventsByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area type="monotone" dataKey="count" fill="#8884d8" stroke="#8884d8" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {/* Event Type Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Event Types</CardTitle>
            <CardDescription>Distribution by event type</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingTypes ? (
              <div className="flex h-72 items-center justify-center">Loading...</div>
            ) : (
              <ChartContainer className="h-72 w-full" config={{}}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={eventTypes}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="eventType" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Player Type Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Player Types</CardTitle>
            <CardDescription>Events by player character</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingPlayers ? (
              <div className="flex h-72 items-center justify-center">Loading...</div>
            ) : (
              <ChartContainer className="h-72 w-full" config={{}}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={playerStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="playerType" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="count" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Query Builder */}
      {/* <QueryEditor /> */}
    </div>
  );
};
